/**
 * Bridges `useRedirectOnCategorySwitch`'s internal `navigate()` call to
 * every OTHER piece of the app that reacts to "the route changed" as if it
 * were a real exit — none of which can be reached synchronously any other
 * way: `useClearExperienceOnUnmount`'s cleanup runs on the OLD route's
 * unmounting component tree, and `LoggedInProvider`'s pathname-watching
 * effect lives in a completely separate ancestor tree above the router. A
 * module-level flag is the one thing all of them can read synchronously,
 * set immediately before `navigate()` runs.
 *
 * Read-only (`isSyntheticCategorySwapPending`) everywhere except
 * `clearSyntheticCategorySwap`, which `useRedirectOnCategorySwitch` alone
 * calls, deferred a tick after `navigate()`. It must stay `true` for the
 * ENTIRE synchronous commit this navigation produces: React fires every
 * unmounting child's cleanup (bottom-up) before any ancestor's effect body
 * that reacts to a changed dependency (also bottom-up) — so if a consumer
 * deep in the tree cleared it, an ancestor checking it moments later in the
 * same commit would already see it as false. Clearing happens out-of-band
 * instead, once nothing in this commit needs it anymore.
 *
 * Without this, a same-entity category flip (experiment <-> personalization)
 * unmounts the old route, whose cleanup wipes every experience-scoped Redux
 * slice as if the merchant were leaving for good — destroying an in-progress,
 * unsaved draft's data before the new route can pick it up — and
 * `LoggedInProvider` separately clears/closes the AI chat, thinking the
 * merchant navigated to an unrelated page.
 */
let syntheticSwapInFlight = false;

export const markSyntheticCategorySwap = () => {
  syntheticSwapInFlight = true;
};

export const clearSyntheticCategorySwap = () => {
  syntheticSwapInFlight = false;
};

export const isSyntheticCategorySwapPending = (): boolean =>
  syntheticSwapInFlight;
